import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { DateTime } from "luxon";
import { Subscription, of, switchMap, finalize } from "rxjs";
import { UpdateBidData } from "../../../interfaces/data-models";
import { BidStatus } from "../../../interfaces/enums";
import { FixedBidSingleReponse } from "../../../interfaces/responses/models";
import { BidService } from "../../../services/bids.service";
import { LocalStorageService } from "../../../services/local-storage.service";
import { ScreenSizeService } from "../../../services/screen-size.service";
import { WebsocketService } from "../../../services/websocket.service";
import { ConfirmDialogComponent } from "../../../shared/confirm-dialog/confirm-dialog.component";
import { GenericMessage } from "../../../shared/generic-message/generic-message.component";
import { RebidComponent } from "../rebid/rebid.component";
import { UpdateFixedRateBidComponent } from "../update-fixed-rate-bid/update-fixed-rate-bid.component";
import { PaymentService } from "src/app/services/payment/user-wallet.service";

@Component({
  selector: "app-fixed-rate-bid",
  templateUrl: "./fixed-rate-bid.component.html",
  styleUrls: ["./fixed-rate-bid.component.css"],
})
export class FixedRateBidComponent implements OnInit, OnDestroy {
  ownBid!: FixedBidSingleReponse;
  partnerBid!: FixedBidSingleReponse;
  isMobile: boolean = false;
  userHasProposedNewOffer: boolean = false;
  partnerHasProposedNewOffer: boolean = false;
  private wsSubscription: Subscription | undefined;
  isPartner: boolean = false;
  transaction_type: string = "";
  status: BidStatus = BidStatus.Accepted;
  bidId: string | null = "";
  isLoading: boolean = false;
  userBidAction: string = "";
  loadingMessage: string = "";
  error: string | null = null;


  remindEnabled: boolean = false; 
  remindUsed: boolean = false; 

  constructor(
    private router: Router,
    private ws: WebsocketService,
    private activatedRoute: ActivatedRoute,
    private bidService: BidService,
    public dialog: MatDialog,
    private localStorageService: LocalStorageService,
    private screenSizeService: ScreenSizeService,
    private paymentService: PaymentService
  ) {}

  ngOnInit(): void {
    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    // Initialize remind flags from local storage if any.
    // If user previously edited the bid and has an outstanding "remind" opportunity,
    // localStorageService.getUpdatedBid() should be true (we set it on edit below).
    const updatedFlag = this.localStorageService.getUpdatedBid?.();
    this.remindEnabled = !!updatedFlag;
    this.remindUsed = false;

    const st = history.state;
    console.log(st);

    if (st.paymentResult === "success" && st.preferentialEditContext) {
      this.isLoading = true;
      this.loadingMessage = "Finalizing your updated preferential bid…";
      const ctx = st.preferentialEditContext;

      // clear so it doesn’t re-run on reload
      this.router.navigate([], { state: {}, replaceUrl: true });

      this.bidService
        .updatePreferentialBid({
          bid_id: ctx.bid_id,
          bid_round_id: ctx.bid_round_id,
          energy_amount: ctx.energy_amount,
          bid_price: ctx.bid_price,
        })
        .subscribe({
          next: () => {
            this.isLoading = false;
            // refresh the bid details
            if (this.bidId) this.getPreferentialExchangeBid(this.bidId);
          },
          error: (err) => {
            this.isLoading = false;
            console.error("Failed to finalize preferential bid update", err);
          },
        });
      return;
    } else if (st.paymentResult === "success" && st.preferentialAcceptContext) {
      this.isLoading = true;
      this.loadingMessage = "Payment successful. Finalizing bid acceptance...";
      const ctx = st.preferentialAcceptContext;
      this.router.navigate([], { state: {}, replaceUrl: true });

      // Call the final acceptance API
      this.bidService
        .completeFixedRateBid(ctx.bid_id, true)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: () => this.navigateTo(`allocation/${ctx.bid_id}`),
          error: (err) => (this.error = "Failed to finalize bid acceptance."),
        });
      return;
    } else {
      this.activatedRoute.params.subscribe((routeParam) => {
        // Clean up any existing WebSocket subscription when route changes
        if (this.wsSubscription) {
          this.wsSubscription.unsubscribe();
          this.wsSubscription = undefined;
          this.ws.closeBidRoundConnection(); // Close specific bid round connection
        }

        const bidId = this.activatedRoute.snapshot.paramMap.get("id");
        this.bidId = bidId;
        if (!bidId) {
          this.navigateTo("market");
          return;
        }

        const bidData = this.localStorageService.getFixRateBid?.();
        this.localStorageService.clearFixRateBid?.();

        if (!bidData) {
          this.getPreferentialExchangeBid(bidId);
        } else {
          console.log(bidData);
          console.log("Using localStorageService");
          this.ownBid = bidData.own_bid;
          this.partnerBid = bidData.partner_bid;
          this.navigateToAllocationIfAllowed();
          this.subscribeToNotifications();
        }
      });

      this.isPartner = this.isSecondaryUser();
      // transaction_type may be set only after ownBid gets loaded; keep safe guard
      this.transaction_type = this.ownBid?.transaction_type ?? "";
    }
  }

  private getPreferentialExchangeBid(bidId: string) {
    this.bidService.getFixRateBid(bidId).subscribe(
      (response) => {
        console.log(response);
        console.log("Using API");
        this.ownBid = response.own_bid;
        this.partnerBid = response.partner_bid;
        this.transaction_type = this.ownBid.transaction_type;

    
        const updatedFlag = this.localStorageService.getUpdatedBid?.();
        this.remindEnabled = !!updatedFlag;
        this.remindUsed = false;

        this.navigateToAllocationIfAllowed();
        this.subscribeToNotifications();
        this.isPartner = this.isSecondaryUser();
      },
      (error) => {
        console.error(error);
        this.navigateTo("market");
      }
    );
    console.log("type of transaction:" + this.ownBid?.transaction_type);
  }

  handleBidUpdate(price: string) {
    this.ownBid.bid_price = price;
    const dialogRef = this.dialog.open(GenericMessage, {
      data: {
        title: "Bid Updated",
        message: `Bid has being updated`,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      if (this.bidId) this.getPreferentialExchangeBid(this.bidId);
    });
  }

  ngOnDestroy(): void {
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
    }
    this.ws.closeBidRoundConnection(); // Close specific bid round connection
  }

  subscribeToNotifications(): void {

    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
    }


    if (this.ownBid && this.ownBid.bid_round) {
      console.log(
        `Subscribing to WebSocket for bid round: ${this.ownBid.bid_round}`
      );


      this.ws.connectBidRound(this.ownBid.bid_round);


      this.wsSubscription = this.ws.bidRoundMessages$.subscribe({
        next: (data) => {
          console.log("Websocket data (Bid Round):", data);
          if (data.type === "strike_price") {

            if (this.isSecondaryUser()) {
              this.bidService
                .completeFixedRateBid(this.ownBid.id, false)
                .subscribe(
                  (completionData) => {
                    if (completionData) {

                      this.navigateTo("market");
                    } else {
                      this.navigateToAllocation();
                    }
                  },
                  (error) => {
                    console.error("Error completing fixed rate bid:", error);
                  }
                );
            } else {
              this.navigateToAllocation();
            }
          }
          if (data.type === "completed_bid_round") {
            this.navigateToAllocation();
          }
          if (data.type === "partner_updated_bid") {
            this.handleBidUpdate(data.price);
          }
        },
        error: (error) => {
          console.error("WebSocket error (Bid Round):", error);
        },
        complete: () => {
          console.log("WebSocket connection closed gracefully (Bid Round)");
        },
      });
    } else {
      console.warn("Cannot subscribe to WebSocket: missing bid round ID");
    }
  }

  navigateToAllocationIfAllowed(): void {
    if (!(this.ownBid && this.partnerBid)) {
      return;
    }
    if (
      (this.partnerBid.status === BidStatus.Rejected ||
        this.partnerBid.status === BidStatus.Accepted) &&
      (this.ownBid.status === BidStatus.Rejected ||
        this.ownBid.status === BidStatus.Accepted)
    ) {
      this.navigateToAllocation();
    }
  }

  navigateToAllocation(): void {
    this.navigateTo(`allocation/${this.ownBid.id}`);
  }

  withdraw(): void {
    const bidId = this.ownBid.id;

    this.isPartner = this.isSecondaryUser();

    let message;

    if (this.isPartner) {
      message = "Are you sure you want to reject the bid?";
    } else {
      message = "Are you sure you want to withdraw your bid?";
    }


    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: "860px",
      data: { message: message }, 
    });


    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.userBidAction = "reject";
        this.isLoading = true;

        this.bidService
          .completeFixedRateBid(bidId, false)
          .subscribe(
            (data) => {
              if (data) {
                // this.navigateTo("market");
                this.navigateToAllocation();
              }
            },
            (error) => {
              console.error("Error loading bid rounds:", error);
            }
          )
          .add(() => {
            this.isLoading = false;
          });
      } else {
        this.userBidAction = "continue";
        this.isLoading = false;
      }
    });
  }

  acceptBid(): void {
    this.isLoading = true;
    this.loadingMessage = "Validating final settlement...";
    this.userBidAction = "accept";
    console.log(this.ownBid.transaction_type);
    const transactionTypeId = this.ownBid.transaction_type === "Buy" ? 1 : 2;
    console.log(transactionTypeId);
    console.log(this.ownBid.strike_price);

    this.paymentService
      .validateBalance({
        bid_price: this.ownBid.strike_price, // Use the final strike_price
        energy_amount: this.ownBid.energy_amount,
        transaction_type_id: transactionTypeId,
        bid_id: this.ownBid.id,
      })
      .pipe(
        switchMap((validationResponse) => {
          console.log(validationResponse);
          if (!validationResponse || typeof validationResponse.required_balance === "undefined") {
            throw new Error("Invalid response from balance validation service.");
          }
          const required = parseFloat(validationResponse.required_balance);
          if (required > 0) {
            this.loadingMessage = "Creating payment intent...";
            return this.paymentService.addTokens({ amount: required, payment_method: "icici_cbdc", device: "web" });
          } else {
            return of({ no_payment_needed: true });
          }
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response.payment_intent) {

            const paymentContext = {
              qr_code: response.qr_code,
              intentLink: response.payment_intent.intent_link,
              txId: response.payment_intent.transaction_id,
              amount: response.payment_intent.amount,
              message: response.payment_intent.message,
              returnRoute: `/fixed-rate-bid/${this.ownBid.id}`,
              preferentialAcceptContext: {
                bid_id: this.ownBid.id,
              },
              display: { title: "Preferential Bid Settlement" },
            };
            sessionStorage.setItem("pendingPaymentContext", JSON.stringify(paymentContext));
            this.router.navigate(["/payment"], { state: paymentContext });
          } else {

            this.loadingMessage = "Accepting bid...";
            this.bidService
              .completeFixedRateBid(this.ownBid.id, true)
              .pipe(finalize(() => (this.isLoading = false)))
              .subscribe({
                next: () => this.navigateTo(`allocation/${this.ownBid.id}`),
                error: (err) => (this.error = "Failed to accept bid."),
              });
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.error = err?.error?.detail || "An error occurred during final validation.";
        },
      });
  }

  remindPartner(): void {

    if (!this.remindEnabled || this.remindUsed) {

      this.dialog.open(GenericMessage, {
        data: { title: "Reminder", message: "Reminder is not available at the moment." },
      });
      return;
    }

    const bidId = this.ownBid.id;

    this.bidService.remindPartnerForPreferentialBid(bidId).subscribe(
      () => {
     
        this.remindUsed = true;
        this.remindEnabled = false;


        try {
          this.localStorageService.setUpdatedBid?.(false);
        } catch (e) {
          // ignore if method not present
        }

        if (this.ownBid.status === BidStatus.Active) {
          this.partnerHasProposedNewOffer = false;
        }
        if (this.ownBid.status === BidStatus.Accepted) {
          this.localStorageService.setUpdatedBid?.(false);
          this.userHasProposedNewOffer = false;
        }
        this.dialog.open(GenericMessage, {
          data: {
            title: "Reminder sent",
            message: `We have sent a reminder to ${this.partnerBid.user_name} regarding your bid`,
          },
        });
      },
      (error) => {
        console.error("Error sending notification:", error);
        this.dialog.open(GenericMessage, {
          data: { title: "Error", message: "Failed to send reminder. Please try again later." },
        });
      }
    );
  }

  canRebid(): boolean {
    return this.ownBid.status === BidStatus.Active && this.partnerBid.status === BidStatus.Accepted;
  }

  isSecondaryUser(): boolean {
    if (!(this.ownBid && this.partnerBid)) {
      return false;
    }
    return this.ownBid.status === BidStatus.Active && this.partnerBid.status === BidStatus.Accepted;
  }

  receiveOfferWithdraw(): boolean {
    return this.ownBid.status === BidStatus.Active && this.partnerBid.status === BidStatus.Accepted;
  }

  canUpdate(): boolean {
    return this.ownBid.status === BidStatus.Accepted && this.partnerBid.status === BidStatus.Active;
  }

  openRebidModal(): void {
    const dialogRef = this.dialog.open(RebidComponent, { data: "1" });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.partnerHasProposedNewOffer = true;
        console.log("User made a new offer:", result);
      }
    });
  }

  getTransactionType(): string {
    return this.transaction_type;
  }

  getUpdatedBidFlagValue(): boolean {
    return this.localStorageService.getUpdatedBid?.();
  }

  openUpdateBidModal(): void {
    const dialogRef = this.dialog.open(UpdateFixedRateBidComponent, {
      data: {
        bidId: this.ownBid.id,
        bidRoundId: this.ownBid.bid_round,
        bidPrice: this.ownBid.strike_price,
        energyAmount: this.ownBid.energy_amount,
        transaction_type: this.ownBid.transaction_type,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === "success") {

        this.remindEnabled = true;
        this.remindUsed = false;


        try {
          this.localStorageService.setUpdatedBid?.(true);
        } catch (e) {
          // ignore
        }


        if (this.bidId) this.getPreferentialExchangeBid(this.bidId);
      } else if (result === "payment-redirect") {

      }
    });
  }

  isActiveSession(): boolean {
    return (
      (this.ownBid.status === BidStatus.Accepted && this.partnerBid.status === BidStatus.Active) ||
      (this.ownBid.status === BidStatus.Active && this.partnerBid.status === BidStatus.Accepted)
    );
  }

  isClosed(): boolean {
    return this.ownBid.status === BidStatus.Closed || this.partnerBid.status === BidStatus.Closed;
  }

  isRejected(): boolean {
    return this.ownBid.status === BidStatus.Rejected || this.partnerBid.status === BidStatus.Rejected;
  }

  navigateTo(path: string): void {
    this.router.navigate([path]);
  }

  parseDate(date: string): DateTime {
    return DateTime.fromFormat(date, "LLLL dd hh:mm a");
  }

  formatTradeTime(date: string): string {
    return this.parseDate(date).toFormat("dd-MMM-yyyy");
  }

  formatTradeTimeYear(date: string): string {
    try {
      let dateTime;
      dateTime = DateTime.fromISO(date);
      if (!dateTime.isValid) {
        dateTime = DateTime.fromFormat(date, "LLLL dd hh:mm a");
      }
      if (!dateTime.isValid) {
        const jsDate = new Date(date);
        if (!isNaN(jsDate.getTime())) {
          dateTime = DateTime.fromJSDate(jsDate);
        } else {
          console.error("Could not parse date:", date);
          return date;
        }
      }
      const day = dateTime.day.toString().padStart(2, "0");
      const month = dateTime.toFormat("MMM");
      const year = dateTime.year;
      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error("Error formatting date:", error, date);
      return date;
    }
  }

  formatBidTime(inputDate: string): string {
    try {
      let dateTime;
      dateTime = DateTime.fromISO(inputDate);
      if (!dateTime.isValid) {
        dateTime = DateTime.fromFormat(inputDate, "LLLL dd hh:mm a");
      }
      if (!dateTime.isValid) {
        const jsDate = new Date(inputDate);
        if (!isNaN(jsDate.getTime())) {
          dateTime = DateTime.fromJSDate(jsDate);
        } else {
          console.error("Could not parse date:", inputDate);
          return inputDate;
        }
      }
      const day = dateTime.day.toString().padStart(2, "0");
      const month = dateTime.toFormat("MMM");
      const hours = dateTime.hour % 12 || 12;
      const minutes = dateTime.minute.toString().padStart(2, "0");
      const ampm = dateTime.hour >= 12 ? "PM" : "AM";
      return `${day}-${month}, ${hours}:${minutes} ${ampm}`;
    } catch (error) {
      console.error("Error formatting date:", error, inputDate);
      return inputDate;
    }
  }
}
